import { Link } from 'react-router-dom';
import { toAbsoluteUrl } from '@/utils';
import { KeenIcon } from '@/components';
import { useMainLayout } from '..';
import { useAuthContext } from '@/auth';
import { useRef, useState } from 'react';
import companySettings from '@/services/setting/companySettings';
import { isSuperAdmin } from '@/utils';
import { toast } from 'sonner';
import Cropper from 'react-easy-crop';
import Moveable from 'react-moveable';
import { Slider } from '@/components/ui/slider';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const DEFAULT_CROP_SIZE = { width: 260, height: 160 };
const MIN_CROP_SIZE = { width: 20, height: 20 };
const SMALL_IMAGE_MIN_ZOOM = 0.2;

const getInitialCropSize = (imageWidth, imageHeight) => {
  if (!imageWidth || !imageHeight) {
    return DEFAULT_CROP_SIZE;
  }

  const scale = Math.min(
    DEFAULT_CROP_SIZE.width / imageWidth,
    DEFAULT_CROP_SIZE.height / imageHeight,
    1,
  );

  const width = Math.max(
    MIN_CROP_SIZE.width,
    Math.min(DEFAULT_CROP_SIZE.width, Math.round(imageWidth * scale)),
  );
  const height = Math.max(
    MIN_CROP_SIZE.height,
    Math.min(DEFAULT_CROP_SIZE.height, Math.round(imageHeight * scale)),
  );

  return { width, height };
};

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = url;
  });

const toPngFileName = (fileName = 'company-logo') => {
  const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
  return `${nameWithoutExt || 'company-logo'}.png`;
};

const getCroppedImageFile = async (imageSrc, pixelCrop, fileName) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(pixelCrop.width));
  canvas.height = Math.max(1, Math.round(pixelCrop.height));
  const ctx = canvas.getContext('2d');

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    canvas.width,
    canvas.height,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Unable to process image'));
        return;
      }
      resolve(new File([blob], toPngFileName(fileName), { type: 'image/png' }));
    }, 'image/png');
  });
};

const HeaderLogo = () => {
  const { auth, updateUser } = useAuthContext();
  const {
    setMobileMegaMenuOpen
  } = useMainLayout();
  const [isLogoModalOpen, setIsLogoModalOpen] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingLogo, setSavingLogo] = useState(false);
  const [settingsData, setSettingsData] = useState(null);
  const [cropImageSrc, setCropImageSrc] = useState('');
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [minZoom, setMinZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [pendingLogoName, setPendingLogoName] = useState('company-logo.jpg');
  const [cropSize, setCropSize] = useState(DEFAULT_CROP_SIZE);
  const [logoPreview, setLogoPreview] = useState('');
  const cropBoxRef = useRef(null);
  const cropContainerRef = useRef(null);
  const resizeRafRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleSidebarOpen = () => {
    setMobileMegaMenuOpen(true);
  };

  const getErrorMessage = (error, fallback) => {
    return error?.message || error?.customMessage || error?.response?.data?.message || fallback;
  };

  const resetCropState = () => {
    if (resizeRafRef.current) {
      cancelAnimationFrame(resizeRafRef.current);
      resizeRafRef.current = null;
    }
    setCropImageSrc('');
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setMinZoom(1);
    setCroppedAreaPixels(null);
    setPendingLogoName('company-logo.jpg');
    setCropSize(DEFAULT_CROP_SIZE);
  };

  const fetchSettings = async () => {
    const response = isSuperAdmin()
      ? await companySettings.superAdminGetCompanySettings()
      : await companySettings.getCompanySettings();

    return response?.data || null;
  };

  const handleOpenLogoModal = () => {
    setIsLogoModalOpen(true);
    resetCropState();
    setLogoPreview(auth?.user?.company_logo || toAbsoluteUrl('/media/app/placeholder-logo.png'));
    setLoadingSettings(true);

    fetchSettings()
      .then((settings) => {
        setSettingsData(settings);
        setLogoPreview(settings?.company_logo || toAbsoluteUrl('/media/app/placeholder-logo.png'));
      })
      .catch((error) => {
        toast.error(getErrorMessage(error, 'Failed to load company settings. You can keep the modal open and try again.'));
      })
      .finally(() => {
        setLoadingSettings(false);
      });
  };

  const handleUpdateLogo = async () => {
    if (!settingsData) {
      toast.error('Company settings are not loaded yet. Please wait a moment and try again.');
      return;
    }

    if (!cropImageSrc || !croppedAreaPixels) {
      toast.warning('Please upload and resize an image first.');
      return;
    }

    try {
      setSavingLogo(true);
      const croppedFile = await getCroppedImageFile(cropImageSrc, croppedAreaPixels, pendingLogoName);
      const formData = new FormData();
      const allowedFields = [
        'account_owner',
        'legal_business_name',
        'country',
        'address',
        'address_2',
        'city',
        'zip_postal_code',
        'phone_number',
        'firm_email',
        'website',
        'tax_id',
        'date_format',
        'time_format',
      ];

      allowedFields.forEach((key) => {
        formData.append(key, settingsData?.[key] ?? '');
      });
      formData.append('company_logo', croppedFile);
      formData.append('_method', 'PUT');

      const response = isSuperAdmin()
        ? await companySettings.superAdminUpdateCompanySettings(formData)
        : await companySettings.updateCompanySettings(formData);

      if (response?.status) {
        const refreshedSettings = await fetchSettings();
        const latestLogo = refreshedSettings?.company_logo || '';
        setSettingsData(refreshedSettings);
        setLogoPreview(latestLogo || toAbsoluteUrl('/media/app/placeholder-logo.png'));
        updateUser({
          company_logo: latestLogo,
          company_name: refreshedSettings?.legal_business_name,
        }, true);
        setIsLogoModalOpen(false);
        resetCropState();
        toast.success('Company logo updated successfully.');
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update company logo.'));
    } finally {
      setSavingLogo(false);
    }
  };

  // Firms upload logos of wildly different aspect ratios. Cap both dimensions and
  // let object-contain letterbox, so a wide logo can't push the topbar off-screen
  // on small viewports or overflow the fixed header height.
  const logoClasses =
    'h-8 sm:h-10 lg:h-[50px] w-auto max-w-[96px] sm:max-w-[160px] lg:max-w-[220px] object-contain';

  return <div className="flex items-center gap-1 grow lg:grow-0 min-w-0"> {/* lg:w-[400px] */}
    {auth.user.referral_attorney !== 1 && (
      <button type="button" onClick={handleSidebarOpen} className="btn btn-icon btn-light btn-clear -ms-2.5 size-10 shrink-0 lg:hidden">
        <KeenIcon icon="menu" className="!text-2xl" />
      </button>
    )}

    <div className="flex items-center gap-2 min-w-0">
      {auth?.user?.company_logo ? (
        <Link to="/" className='flex items-center gap-2 min-w-0'>
          <img src={auth?.user?.company_logo} className={logoClasses} alt="logo" />
        </Link>
      ) : (
        <button
          type="button"
          className="cursor-pointer min-w-0"
          onClick={handleOpenLogoModal}
          disabled={loadingSettings || savingLogo}
        >
          <img src={toAbsoluteUrl('/media/app/placeholder-logo.png')} className={`${logoClasses} mx-auto dark:rounded-md`} alt="logo" />
        </button>
        // <h3 className="text-gray-900 font-semibold text-xl hidden md:block">{auth?.user?.company_name}</h3>
      )
      }
      {/* <img src={toAbsoluteUrl('/media/app/oak-branding-woodmark-black.svg')} className="dark:hidden h-[28px]" alt="logo" />
          <img src={toAbsoluteUrl('/media/app/oak-branding-woodmark-white.svg')} className="hidden dark:inline-block h-[28px]" alt="logo" /> */}
    </div>

    {/* {desktopMode && <div className="lg:flex items-center">
          <div className="border-e border-gray-200 h-5 mx-4"></div>

          <Menu className="menu-default">
            <MenuItem toggle="dropdown" trigger="hover" dropdownProps={{
          placement: isRTL() ? 'bottom-end' : 'bottom-start',
          modifiers: [{
            name: 'offset',
            options: {
              offset: [0, 10] // [skid, distance]
            }
          }]
        }}>
              <MenuToggle className="text-gray-900 text-sm font-medium">
                TeamRafa
                <MenuArrow>
                  <KeenIcon icon="down" />
                </MenuArrow>
              </MenuToggle>
              <MenuSub className="menu-default w-48 py-2">
                {teams.map((team, index) => <MenuItem key={index}>
                    <MenuLink path={team.path}>
                      {team.icon && <MenuIcon>
                          <KeenIcon icon={team.icon} />
                        </MenuIcon>}
                      <MenuTitle>{team.title}</MenuTitle>
                    </MenuLink>
                  </MenuItem>)}
              </MenuSub>
            </MenuItem>
          </Menu>

          <div className="border-e border-gray-200 h-5 mx-4"></div>

          <Menu className="menu-default w-[120px]">
            <MenuItem toggle="dropdown" trigger="hover" dropdownProps={{
          placement: isRTL() ? 'bottom-end' : 'bottom-start',
          modifiers: [{
            name: 'offset',
            options: {
              offset: [0, 0] // [skid, distance]
            }
          }]
        }}>
              <MenuToggle className="btn btn-light btn-sm flex-nowrap">
                <span className="flex items-center me-1">
                  <KeenIcon icon="shop" className="!text-md" />
                </span>
                <span className="hidden md:inline text-nowrap">RafaShop</span>
                <span className="flex items-center">
                  <KeenIcon icon="down" className="!text-xs" />
                </span>
              </MenuToggle>
              <MenuSub className="menu-default w-48 py-2 scrollable-y max-h-[250px]">
                {months.map((item, index) => <MenuItem key={index}>
                    <MenuLink path="#">
                      <MenuTitle>{item.title}</MenuTitle>
                    </MenuLink>
                  </MenuItem>)}
              </MenuSub>
            </MenuItem>
          </Menu>
        </div>} */}

    <Dialog
      open={isLogoModalOpen}
      onOpenChange={(open) => {
        setIsLogoModalOpen(open);
        if (!open) {
          resetCropState();
        }
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Upload Company Logo</DialogTitle>
        </DialogHeader>

        <DialogBody className="space-y-4 pb-2 !shadow-none !border-0">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <img
                src={logoPreview || toAbsoluteUrl('/media/app/placeholder-logo.png')}
                className="w-20 h-20 rounded-full object-contain border border-gray-200 bg-gray-500"
                alt="Current logo preview"
              />
              <span className="text-sm text-gray-700">Upload and resize your company logo</span>
            </div>

            <button
              type="button"
              className="btn btn-sm btn-light"
              onClick={() => fileInputRef.current?.click()}
              disabled={loadingSettings || savingLogo}
            >
              Choose Image
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              className="hidden"
              onChange={(event) => {
                const selectedFile = event.target.files?.[0];
                event.target.value = '';

                if (!selectedFile) {
                  return;
                }

                const fileReader = new FileReader();
                fileReader.onload = async () => {
                  const uploadedImageSrc = String(fileReader.result);
                  const image = await createImage(uploadedImageSrc);
                  const isSmallImage =
                    image.width < DEFAULT_CROP_SIZE.width || image.height < DEFAULT_CROP_SIZE.height;

                  setPendingLogoName(selectedFile.name || 'company-logo.jpg');
                  setCropImageSrc(uploadedImageSrc);
                  setCrop({ x: 0, y: 0 });
                  setZoom(1);
                  setMinZoom(isSmallImage ? SMALL_IMAGE_MIN_ZOOM : 1);
                  setCroppedAreaPixels(null);
                  setCropSize(getInitialCropSize(image.width, image.height));
                };
                fileReader.readAsDataURL(selectedFile);
              }}
            />
          </div>

          <div className="relative h-[320px] w-full overflow-hidden rounded-md bg-gray-100">
            {cropImageSrc ? (
              <div ref={cropContainerRef} className="absolute inset-0">
                <Cropper
                  image={cropImageSrc}
                  crop={crop}
                  zoom={zoom}
                  minZoom={minZoom}
                  cropSize={cropSize}
                  aspect={cropSize.width / cropSize.height}
                  cropShape="rect"
                  showGrid
                  objectFit="contain"
                  restrictPosition={false}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={(_, areaPixels) => setCroppedAreaPixels(areaPixels)}
                />

                <div
                  ref={cropBoxRef}
                  className="pointer-events-none absolute left-1/2 top-1/2 border-2 border-white"
                  style={{
                    width: `${cropSize.width}px`,
                    height: `${cropSize.height}px`,
                    transform: 'translate(-50%, -50%)',
                  }}
                />

                <Moveable
                  target={cropBoxRef.current}
                  resizable
                  keepRatio={false}
                  edge
                  draggable={false}
                  origin={false}
                  throttleResize={1}
                  renderDirections={['n', 's', 'e', 'w', 'nw', 'ne', 'sw', 'se']}
                  onResizeStart={({ set }) => {
                    set([cropSize.width, cropSize.height]);
                  }}
                  onResize={({ target, width, height }) => {
                    const containerRect = cropContainerRef.current?.getBoundingClientRect();
                    const maxWidth = Math.max(MIN_CROP_SIZE.width, Math.floor(containerRect?.width || DEFAULT_CROP_SIZE.width));
                    const maxHeight = Math.max(MIN_CROP_SIZE.height, Math.floor(containerRect?.height || DEFAULT_CROP_SIZE.height));
                    const nextWidth = Math.min(maxWidth, Math.max(MIN_CROP_SIZE.width, Math.round(width)));
                    const nextHeight = Math.min(maxHeight, Math.max(MIN_CROP_SIZE.height, Math.round(height)));

                    target.style.width = `${nextWidth}px`;
                    target.style.height = `${nextHeight}px`;

                    if (resizeRafRef.current) {
                      cancelAnimationFrame(resizeRafRef.current);
                    }

                    resizeRafRef.current = requestAnimationFrame(() => {
                      setCropSize({ width: nextWidth, height: nextHeight });
                    });
                  }}
                />
              </div>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500">
                Select an image to start resizing
              </div>
            )}
          </div>

          <div>
            <div className="mb-2 text-sm text-gray-700">Zoom: {zoom.toFixed(1)}x</div>
            <Slider
              min={minZoom}
              max={3}
              step={0.1}
              value={[zoom]}
              onValueChange={(nextValue) => setZoom(nextValue[0] ?? minZoom)}
            />
          </div>
        </DialogBody>

        <DialogFooter className="gap-2 px-5 pb-5">
          <button
            type="button"
            className="btn btn-sm btn-light"
            onClick={() => {
              setIsLogoModalOpen(false);
              resetCropState();
            }}
            disabled={savingLogo}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={handleUpdateLogo}
            disabled={savingLogo || loadingSettings}
          >
            {savingLogo ? 'Saving...' : 'Save Logo'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>;
};
export { HeaderLogo };
